import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Loader2, Lock, ChevronRight, ChevronLeft, AlertCircle, CheckCircle2, Plus, Network, FileSignature } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtDateTimeShort } from "@/lib/dates";

const STATUS_LABELS = {
  concern_raised: "1. Concern raised",
  triaged: "2. Triaged",
  under_investigation: "3. Under investigation",
  section_42_enquiry: "4. Section 42 enquiry",
  multi_agency_review: "5. Multi-agency review",
  outcome_agreed: "6. Outcome agreed",
  action_plan_in_place: "7. Action plan in place",
  closed: "8. Closed",
  lessons_learned: "9. Lessons learned",
};

const ORDER = Object.keys(STATUS_LABELS);

export default function Safeguarding() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/safeguarding").then(({ data }) => setItems(data.items || [])).catch(() => setForbidden(true));
  useEffect(() => {
    if (user.role === "family_member") { setForbidden(true); return; }
    if (["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "super_admin"].includes(user.role)) {
      load();
    }
  // eslint-disable-next-line
  }, [user.role]);

  if (["care_worker", "bank_worker", "team_leader"].includes(user.role)) return <RaiseConcernView />;
  if (forbidden) return <div className="bg-white border border-slate-200 rounded-lg p-6 text-slate-600 inline-flex items-center gap-2"><Lock className="h-4 w-4" /> Restricted module.</div>;

  if (openId) {
    return <CaseDetail id={openId} onBack={() => { setOpenId(null); load(); }} userRole={user.role} />;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Safeguarding</h1>
          <p className="text-sm text-slate-600">Care Act 2014 / Working Together to Safeguard Children 2023. 9-step lifecycle.</p>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="safeguarding-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="text-left py-2 px-3">Raised</th>
              <th className="text-left py-2 px-3">Description</th>
              <th className="text-left py-2 px-3">Raised by</th>
              <th className="text-left py-2 px-3">Stage</th>
              <th className="text-left py-2 px-3">Open actions</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-3 text-slate-700">{fmtDate(s.created_at)}</td>
                <td className="py-3 px-3">
                  <p className="text-slate-900 font-medium flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-red-500" /> Concern</p>
                  <p className="text-xs text-slate-500 line-clamp-1">{s.concern_description}</p>
                </td>
                <td className="py-3 px-3 text-slate-700">{s.raised_by_name}</td>
                <td className="py-3 px-3 text-xs">{STATUS_LABELS[s.case_status] || s.case_status}</td>
                <td className="py-3 px-3 text-xs">{(s.actions || []).filter((a) => a.status !== "completed").length} open</td>
                <td className="py-3 px-3">
                  <button onClick={() => setOpenId(s.id)} data-testid={`open-sg-${s.id}`} className="text-[#0D7377] inline-flex items-center gap-1 text-sm font-medium">Open <ChevronRight className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-500">No safeguarding records.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CaseDetail({ id, onBack, userRole }) {
  const [rec, setRec] = useState(null);
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = () => api.get(`/safeguarding/${id}`).then(({ data }) => setRec(data.record));
  useEffect(() => {
    load();
    api.get("/staff").then(({ data }) => setStaff(data.items || [])).catch(() => {});
  // eslint-disable-next-line
  }, [id]);

  if (!rec) return <div className="text-slate-500">Loading…</div>;
  const idx = ORDER.indexOf(rec.case_status);
  const isNI = ["nominated_individual", "super_admin"].includes(userRole);

  return (
    <div className="space-y-5" data-testid="safeguarding-case-detail">
      <button onClick={onBack} className="text-sm text-slate-600 hover:text-[#0D7377] inline-flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> All cases</button>

      <header className="bg-white rounded-lg border border-red-200 shadow-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-6 w-6 text-red-600" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Safeguarding case</h1>
              <p className="text-sm text-slate-600">Raised {fmtDate(rec.created_at)} by {rec.raised_by_name}</p>
            </div>
          </div>
          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid="case-status-badge">{STATUS_LABELS[rec.case_status] || rec.case_status}</span>
        </div>

        {/* Pipeline */}
        <ol className="mt-5 grid grid-cols-3 md:grid-cols-9 gap-1 text-[10px]">
          {ORDER.map((s, i) => (
            <li key={s} className={`px-2 py-1.5 rounded ${i < idx ? "bg-emerald-50 text-emerald-800" : i === idx ? "bg-[#0D7377] text-white font-semibold" : "bg-slate-100 text-slate-500"}`}>
              {STATUS_LABELS[s].replace(/^\d+\.\s/, "")}
            </li>
          ))}
        </ol>
      </header>

      <nav className="border-b border-slate-200 flex gap-1 overflow-x-auto" role="tablist">
        {[["overview", "Overview"], ["actions", "Action plan"], ["agencies", "Multi-agency"], ["history", "History"], ["closure", "Closure"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sg-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${tab === k ? "text-[#0D7377] border-b-2 border-[#0D7377]" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
        ))}
      </nav>

      {tab === "overview" && <OverviewSection rec={rec} reload={load} userRole={userRole} />}
      {tab === "actions" && <ActionsSection rec={rec} reload={load} staff={staff} userRole={userRole} />}
      {tab === "agencies" && <MultiAgencySection rec={rec} reload={load} userRole={userRole} />}
      {tab === "history" && <HistorySection rec={rec} />}
      {tab === "closure" && <ClosureSection rec={rec} reload={load} isNI={isNI} />}
    </div>
  );
}

function OverviewSection({ rec, reload, userRole }) {
  const isMgmt = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "super_admin"].includes(userRole);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 lg:col-span-2 space-y-3">
        <p className="text-xs uppercase tracking-wider text-slate-500">Concern description</p>
        <p className="text-sm text-slate-800 whitespace-pre-wrap">{rec.concern_description}</p>
        <p className="text-xs uppercase tracking-wider text-slate-500 mt-3">Immediate action taken</p>
        <p className="text-sm text-slate-800">{rec.immediate_action || "None recorded."}</p>
        {rec.investigation && (
          <>
            <p className="text-xs uppercase tracking-wider text-slate-500 mt-3">Investigation</p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">{rec.investigation.findings || "—"}</p>
          </>
        )}
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-2">
        <p className="text-xs uppercase tracking-wider text-slate-500">Move to next stage</p>
        <NextStageButtons rec={rec} reload={reload} disabled={!isMgmt} />
      </div>
    </div>
  );
}

function NextStageButtons({ rec, reload, disabled }) {
  const idx = ORDER.indexOf(rec.case_status);
  const next = idx + 1 < ORDER.length ? ORDER[idx + 1] : null;
  const allowedTargets = ORDER.slice(idx + 1, Math.min(idx + 3, ORDER.length)).filter((s) => s !== "closed");
  const [chosen, setChosen] = useState(next || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true); setError("");
    try {
      await api.put(`/safeguarding/${rec.id}/status`, { status: chosen, note });
      setNote(""); reload();
    } catch (e) { setError(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  if (rec.case_status === "closed") return <p className="text-xs text-slate-500">Case is closed. Use Closure tab to record lessons learned.</p>;

  return (
    <div className="space-y-2">
      <select value={chosen} onChange={(e) => setChosen(e.target.value)} disabled={disabled} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="next-stage-select">
        {allowedTargets.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
      </select>
      <textarea required minLength={5} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (required, min 5 chars)" rows={3} disabled={disabled} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="next-stage-note" />
      {error && <p className="text-xs text-red-700">{error}</p>}
      <button onClick={submit} disabled={busy || disabled || !chosen || note.length < 5} data-testid="next-stage-submit" className="w-full bg-[#0D7377] hover:bg-[#0A5C5F] text-white py-2 rounded-md text-sm font-medium disabled:opacity-50">{busy ? "Saving…" : "Advance to " + (STATUS_LABELS[chosen] || "next")}</button>
    </div>
  );
}

function ActionsSection({ rec, reload, staff, userRole }) {
  const isMgmt = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "super_admin"].includes(userRole);
  const [form, setForm] = useState({ description: "", owner_user_id: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const [completionNote, setCompletionNote] = useState({});

  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/safeguarding/${rec.id}/actions`, form);
      setForm({ description: "", owner_user_id: "", due_date: "" }); reload();
    } finally { setBusy(false); }
  };

  const completeAction = async (actionId) => {
    const note = completionNote[actionId] || "";
    if (note.length < 5) return;
    await api.put(`/safeguarding/${rec.id}/actions/${actionId}/complete`, { completion_note: note });
    setCompletionNote((s) => ({ ...s, [actionId]: "" })); reload();
  };

  return (
    <div className="space-y-4">
      {isMgmt && (
        <form onSubmit={add} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 grid grid-cols-1 md:grid-cols-4 gap-2 items-end" data-testid="add-action-form">
          <textarea required minLength={10} placeholder="Action description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-desc" />
          <select required value={form.owner_user_id} onChange={(e) => setForm({ ...form, owner_user_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-owner">
            <option value="">Owner…</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.full_name} — {s.role.replace(/_/g, " ")}</option>)}
          </select>
          <input required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-due" />
          <button disabled={busy} className="md:col-span-4 inline-flex items-center justify-center gap-1.5 bg-[#0D7377] text-white py-2 rounded-md text-sm font-medium" data-testid="add-action-button"><Plus className="h-4 w-4" /> Add action</button>
        </form>
      )}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-3">
        <p className="text-xs uppercase tracking-wider text-slate-500">Action plan ({(rec.actions || []).length})</p>
        {(rec.actions || []).length === 0 && <p className="text-sm text-slate-500">No actions yet.</p>}
        {(rec.actions || []).map((a) => (
          <div key={a.id} className={`border rounded-md p-3 ${a.status === "completed" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid={`action-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-slate-900">{a.description}</p>
                <p className="text-xs text-slate-600 mt-0.5">Owner: {a.owner_name} · Due {a.due_date}</p>
                {a.completion_note && <p className="text-xs text-emerald-800 mt-1 italic">"{a.completion_note}" — {a.completed_by_name} on {fmtDate(a.completed_at)}</p>}
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-300">{a.status}</span>
            </div>
            {a.status !== "completed" && (
              <div className="mt-2 flex gap-2">
                <input value={completionNote[a.id] || ""} onChange={(e) => setCompletionNote((s) => ({ ...s, [a.id]: e.target.value }))} placeholder="Completion note (min 5 chars)" className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded" data-testid={`complete-note-${a.id}`} />
                <button onClick={() => completeAction(a.id)} className="text-xs px-3 py-1 bg-emerald-600 text-white rounded inline-flex items-center gap-1" data-testid={`complete-action-${a.id}`}><CheckCircle2 className="h-3 w-3" /> Complete</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MultiAgencySection({ rec, reload, userRole }) {
  const isMgmt = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "super_admin"].includes(userRole);
  const [form, setForm] = useState({ agency: "local_authority", contact_name: "", contact_phone: "", contact_email: "", reference_number: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/safeguarding/${rec.id}/multi-agency`, form); setForm({ ...form, contact_name: "", contact_phone: "", contact_email: "", reference_number: "", notes: "" }); reload(); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      {isMgmt && (
        <form onSubmit={add} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="multi-agency-form">
          <select value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm">
            <option value="local_authority">Local Authority</option>
            <option value="police">Police</option>
            <option value="icb">Integrated Care Board</option>
            <option value="ambulance">Ambulance</option>
            <option value="lado">LADO</option>
            <option value="cqc">CQC</option>
            <option value="other">Other</option>
          </select>
          <input required placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input placeholder="Phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input placeholder="Email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input placeholder="Reference number" value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm md:col-span-2" />
          <button disabled={busy} data-testid="add-agency-button" className="md:col-span-3 inline-flex items-center justify-center gap-1.5 bg-[#0D7377] text-white py-2 rounded-md text-sm font-medium"><Network className="h-4 w-4" /> Record referral</button>
        </form>
      )}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Agencies involved</p>
        {(rec.multi_agency || []).length === 0 ? <p className="text-sm text-slate-500">No multi-agency referrals recorded.</p> : (
          <ul className="divide-y divide-slate-100">
            {(rec.multi_agency || []).map((a) => (
              <li key={a.id} className="py-3" data-testid={`agency-${a.id}`}>
                <p className="text-sm font-medium text-slate-900 capitalize">{a.agency.replace(/_/g, " ")}</p>
                <p className="text-xs text-slate-600">{a.contact_name} · {a.contact_phone} · Ref {a.reference_number || "—"}</p>
                {a.notes && <p className="text-xs text-slate-700 italic mt-0.5">{a.notes}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function HistorySection({ rec }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
      <ol className="space-y-3" data-testid="status-history">
        {(rec.status_history || []).map((h, i) => (
          <li key={i} className="border-l-2 border-[#0D7377] pl-3">
            <p className="text-sm font-medium text-slate-900">{STATUS_LABELS[h.status] || h.status}</p>
            <p className="text-xs text-slate-600">{fmtDateTimeShort(h.changed_at)} by {h.changed_by_name}</p>
            <p className="text-sm text-slate-700 mt-0.5">{h.note}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ClosureSection({ rec, reload, isNI }) {
  const [form, setForm] = useState({ outcome_summary: "", lessons_learned: "", confirmation: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await api.put(`/safeguarding/${rec.id}/close`, form); reload(); } catch (er) { setError(er.response?.data?.detail || er.message); } finally { setBusy(false); }
  };
  if (rec.case_status === "closed") {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 space-y-2" data-testid="case-closed-banner">
        <p className="text-sm font-semibold text-emerald-900 inline-flex items-center gap-2"><FileSignature className="h-4 w-4" /> Case closed and countersigned by Nominated Individual</p>
        <p className="text-xs text-emerald-800">{rec.ni_countersigned_by_name} · {fmtDate(rec.ni_countersigned_at)}</p>
        {rec.outcome_summary && <p className="text-sm text-slate-800 mt-2"><strong>Outcome:</strong> {rec.outcome_summary}</p>}
        {rec.lessons_learned && <p className="text-sm text-slate-800"><strong>Lessons learned:</strong> {rec.lessons_learned}</p>}
      </div>
    );
  }
  if (!isNI) return <div className="bg-white border border-slate-200 rounded-lg p-5 text-sm text-slate-600 inline-flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Closure can only be countersigned by the Nominated Individual.</div>;
  return (
    <form onSubmit={submit} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-3" data-testid="closure-form">
      <p className="text-xs uppercase tracking-wider text-slate-500">Nominated Individual countersignature required (Care Act 2014, Reg 13)</p>
      <textarea required minLength={50} placeholder="Outcome summary (min 50 chars)" rows={4} value={form.outcome_summary} onChange={(e) => setForm({ ...form, outcome_summary: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="closure-outcome" />
      <textarea placeholder="Lessons learned (optional but recommended)" rows={3} value={form.lessons_learned} onChange={(e) => setForm({ ...form, lessons_learned: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="closure-lessons" />
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={form.confirmation} onChange={(e) => setForm({ ...form, confirmation: e.target.checked })} className="mt-1" data-testid="closure-confirm" />
        <span>I, as Nominated Individual, confirm I have reviewed this case and approve closure.</span>
      </label>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <button disabled={busy || !form.confirmation} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-semibold disabled:opacity-50" data-testid="closure-submit">{busy ? "Closing…" : "Countersign and close"}</button>
    </form>
  );
}

export function RaiseConcernPage() {
  return <RaiseConcernView />;
}

function RaiseConcernView() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ service_user_id: "", concern_description: "", immediate_action: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  useEffect(() => { api.get("/service-users").then(({ data }) => setUsers(data.items || [])); }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/safeguarding/concern", form);
      setDone(true);
      setTimeout(() => nav("/visits"), 2200);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-white border border-emerald-200 rounded-lg shadow-sm p-8 text-center" data-testid="safeguarding-success">
        <ShieldAlert className="h-10 w-10 text-emerald-600 mx-auto" />
        <h2 className="mt-3 font-heading text-xl font-semibold text-slate-900">Concern received</h2>
        <p className="mt-1 text-sm text-slate-600">Management has been notified. Thank you for raising this.</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto bg-white border border-red-200 rounded-lg shadow-sm p-6 space-y-4">
      <div className="flex items-center gap-2 text-red-700">
        <ShieldAlert className="h-5 w-5" />
        <h1 className="font-heading text-xl font-semibold">Raise a safeguarding concern</h1>
      </div>
      <p className="text-sm text-slate-600">Concerns are sent immediately to the Registered Manager, Nominated Individual and Compliance Manager. Records are restricted.</p>
      <form onSubmit={submit} className="space-y-3" data-testid="safeguarding-form">
        <select required value={form.service_user_id} onChange={(e) => setForm({ ...form, service_user_id: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md">
          <option value="">Service user…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <textarea required placeholder="What did you observe or hear?" rows={5} value={form.concern_description} onChange={(e) => setForm({ ...form, concern_description: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="safeguarding-description" />
        <textarea placeholder="Immediate action taken" rows={2} value={form.immediate_action} onChange={(e) => setForm({ ...form, immediate_action: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md" />
        {err && <p className="text-sm text-red-700">{err}</p>}
        <button disabled={busy} data-testid="safeguarding-submit-button" className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-md min-h-[48px]">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit concern
        </button>
      </form>
    </div>
  );
}
